import type { Design, RendererContext, RenderParams, UchiwaType } from '../types';
import { UCHIWA_SIZES } from '../types';
import { getRenderer, createPreviewContext, createPrintContext } from './styles/base';

// Register all built-in styles
import './styles/simple';
import './styles/bold';
import './styles/purupuru';
import './styles/glitter';

export function mmToPx(mm: number, dpi = 96): number {
  return (mm * dpi) / 25.4;
}

export interface CanvasSize {
  width: number;
  height: number;
}

export function getCanvasSize(uchiwaType: UchiwaType, rc: RendererContext): CanvasSize {
  const size = UCHIWA_SIZES[uchiwaType === 'mini' ? 'regular' : uchiwaType] ?? UCHIWA_SIZES.regular;
  return {
    width: Math.round(mmToPx(size.printableArea.width, rc.dpi)),
    height: Math.round(mmToPx(size.printableArea.height, rc.dpi)),
  };
}

export function designToRenderParams(design: Design, canvasSize: CanvasSize): RenderParams {
  return {
    text: design.text,
    textColor: design.style.textColor,
    backgroundColor: design.style.backgroundColor,
    secondaryColor: design.style.secondaryColor,
    fontSize: design.style.fontSize,
    textOrientation: design.style.textOrientation,
    canvasWidth: canvasSize.width,
    canvasHeight: canvasSize.height,
    effects: design.style.effects,
  };
}

/**
 * Render design onto a canvas element (preview mode)
 */
export function renderPreview(
  canvas: HTMLCanvasElement,
  design: Design,
  scaleFactor = 1,
): void {
  const rc = createPreviewContext(scaleFactor);
  const size = getCanvasSize(design.uchiwaType, rc);

  canvas.width = size.width;
  canvas.height = size.height;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const renderer = getRenderer(design.style.preset);
  if (!renderer) {
    console.warn(`No renderer found for preset: ${design.style.preset}`);
    return;
  }

  const params = designToRenderParams(design, size);
  renderer.render(ctx, params, rc);
}

/**
 * Render design to an offscreen canvas and return data URL (for thumbnails)
 */
export function renderToDataUrl(design: Design, scale = 0.5): string {
  const rc = createPreviewContext(scale);
  const size = getCanvasSize(design.uchiwaType, rc);

  const canvas = document.createElement('canvas');
  canvas.width = size.width;
  canvas.height = size.height;

  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  const renderer = getRenderer(design.style.preset);
  if (!renderer) return '';

  renderer.render(ctx, designToRenderParams(design, size), rc);
  return canvas.toDataURL('image/png');
}

/**
 * Render at print resolution
 */
export function renderForPrint(
  canvas: HTMLCanvasElement,
  design: Design,
): void {
  const rc = createPrintContext();
  const size = getCanvasSize(design.uchiwaType, rc);

  canvas.width = size.width;
  canvas.height = size.height;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const renderer = getRenderer(design.style.preset);
  if (!renderer) return;

  renderer.render(ctx, designToRenderParams(design, size), rc);
}
